import { Calendar, Utensils, Clock, ShoppingCart } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useMealPlan } from "../contexts/MealPlanContext";
import { useMeals } from "../contexts/UserMealsContext";
import { useShoppingList } from "../contexts/ShoppingListContext";
import { useReminders } from "../contexts/RemindersContext";
import { MealPlannerUtils } from "../utils/MealPlanUtils/MealPlannerUtils";
import { getReminderUrgency, sortRemindersByUrgency } from "../utils/reminderUtils";
import { LoadingComponent } from "../components/LoadingComponent";
import { UpcomingMealsComponent } from "../components/DashboardComponents/UpcomingMealsComponent";
import { ShoppingListComponent } from "../components/DashboardComponents/ShoppingListComponent";
import { RemindersComponent } from "../components/DashboardComponents/RemindersComponent";

import { useNavigate } from "react-router";

export function DashboardPage() {
  const { mealPlan } = useMealPlan();
  const { user } = useAuth();
  const { meals } = useMeals();
  const { shoppingListObject: shoppingList } = useShoppingList();
  const { reminders } = useReminders();
  const navigate = useNavigate();

  const stats = [
    {
      label: "Meals Planned",
      value: mealPlan?.length || 0,
      unit: "",
      icon: Calendar,
      navigateTo: "/MealPlan",
    },
    {
      label: "Recipes Saved",
      value: meals?.length || 0,
      unit: "",
      icon: Utensils,
      navigateTo: "/MealList",
    },
    {
      label: "Weekly Prep Time",
      value:
        mealPlan?.reduce((sum, entry) => sum + (entry.timeMins || 0), 0) || 0,
      unit: "min",
      icon: Clock,
      navigateTo: "/MealPlan",
    },
    {
      label: "Shopping List Items",
      value: shoppingList?.length || 0,
      unit: "",
      icon: ShoppingCart,
      navigateTo: "/ShoppingList",
    },
  ];

  const upcomingMeals = MealPlannerUtils.returnUpcomingMeals(mealPlan);
  const shoppingListItemsToGet =
    shoppingList?.filter((item) => !item.checked) || [];
  const remindersToShow = sortRemindersByUrgency(
    (reminders || []).filter(
      (reminder) => getReminderUrgency(reminder) !== "FRESH",
    ),
  );

  if (!mealPlan || !meals || !shoppingList || !reminders) {
    return <LoadingComponent message={"Loading dashboard..."} />;
  }

  console.log("Loaded: ", mealPlan);

  const upcomingMealsComponentOnClickParent = () => {
    navigate("/MealPlan");
  };

  const upcomingMealsComponentOnClickChild = (e) => {
    e.stopPropagation();
    navigate("/MealList");
  };

  const onClockShoppingList = () => {
    navigate("/ShoppingList");
  };

  const onClickReminders = () => {
    navigate("/Reminders");
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-semibold text-gray-900 mb-2">
          Welcome back, {user?.username}
        </h1>
        <p className="text-gray-600">Here's what's cooking this week</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div
              key={stat.label}
              className="bg-white rounded-lg p-6 border border-gray-200"
              onClick={() => navigate(stat.navigateTo)}
              style={{ cursor: "pointer" }}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-gray-700" />
                </div>
              </div>
              <div className="text-2xl font-semibold text-gray-900 mb-1">
                {stat.value}
              </div>
              <div className="text-sm text-gray-600">{stat.label}</div>
            </div>
          );
        })}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Upcoming Meals */}
        <UpcomingMealsComponent
          upcomingMeals={upcomingMeals}
          onClickParent={upcomingMealsComponentOnClickParent}
          onClickChild={upcomingMealsComponentOnClickChild}
        />

        {/* Shopping List */}
        <ShoppingListComponent
          shoppingListItemsToGet={shoppingListItemsToGet}
          onClick={onClockShoppingList}
        />

        {/* Reminders */}
        <RemindersComponent
          remindersToShow={remindersToShow}
          onClick={onClickReminders}
        />
      </div>
    </div>
  );
}
